import { PictureType } from '~/common/entities/Pictures/PictureTypes';
import { usePictureService } from '~/common/services/PictureService';
import { useToast } from '@chakra-ui/react';
import useTranslate from './translate';

export type Base64Strings = {
  base64: string[];
};

export function usePictureUpload() {
  const toast = useToast();
  const pictureService = usePictureService();
  const translate = useTranslate();

  const showErrorToast = () => {
    toast({
      title: translate('pictureUploadButton.errorToast.title'),
      description: translate('pictureUploadButton.errorToast.description'),
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
  };

  const showInfoToast = () => {
    toast({
      title: translate('pictureUploadButton.infoToast.title'),
      description: translate('pictureUploadButton.infoToast.description'),
      status: 'info',
      duration: 5000,
      isClosable: true,
    });
  };

  const showSuccessToast = () => {
    toast({
      title: translate('pictureUploadButton.successToast.title'),
      description: translate('pictureUploadButton.successToast.description'),
      status: 'success',
      duration: 5000,
      isClosable: true,
    });
  };

  const uploadPhoto = (
    picturesAsBase64: string[],
    pictureType: PictureType,
    projectOrJobId: string,
  ) => {
    return pictureService.uploadPictures(
      picturesAsBase64,
      pictureType,
      projectOrJobId,
      0,
      showSuccessToast,
    );
  };

  const resizeAndUploadPictures = async (
    picturesAsBase64: string[],
    pictureType: PictureType,
    projectOrJobId: string,
  ) => {
    if (!picturesAsBase64 || picturesAsBase64.length === 0) return;
    showInfoToast();
    if (picturesAsBase64.length > 0) {
      return uploadPhoto(picturesAsBase64, pictureType, projectOrJobId);
    } else {
      showErrorToast();
      throw new Error('No pictures to upload');
    }
  };

  const handleFileChange = async (
    base64Strings: Base64Strings,
    pictureType: PictureType,
    projectOrJobId: string,
  ) => {
    const picturesAsBase64WithPrefix = base64Strings.base64;
    const picturesAsBase64: string[] = [];
    picturesAsBase64WithPrefix.forEach((pic) => {
      if (pic.startsWith('data:image/jpeg;base64,')) {
        picturesAsBase64.push(pic.replace('data:image/jpeg;base64,', ''));
      } else if (pic.startsWith('data:image/png;base64,')) {
        picturesAsBase64.push(pic.replace('data:image/png;base64,', ''));
      } else {
        picturesAsBase64.push(pic);
      }
    });
    return resizeAndUploadPictures(
      picturesAsBase64,
      pictureType,
      projectOrJobId,
    );
  };
  const convertFilesToBase64StringAndHandleFileChange = (
    files: File[],
    pictureType: PictureType,
    projectOrJobId: string,
    customHandleFileChange?: (base64StringsArray: Base64Strings) => void,
  ) => {
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64StringsArray: Base64Strings = { base64: [] };
        const binaryStr = reader.result;
        if (typeof binaryStr === 'string') {
          base64StringsArray.base64.push(binaryStr);
        } else if (binaryStr instanceof ArrayBuffer) {
          const base64 = btoa(
            new Uint8Array(binaryStr).reduce(
              (data, byte) => data + String.fromCharCode(byte),
              '',
            ),
          );
          base64StringsArray.base64.push(base64);
        }
        if (customHandleFileChange) customHandleFileChange(base64StringsArray);
        else
          void handleFileChange(
            base64StringsArray,
            pictureType,
            projectOrJobId,
          );
      };
      reader.readAsArrayBuffer(file);
    });
  };

  return { handleFileChange, convertFilesToBase64StringAndHandleFileChange };
}
